import { createApiKey, revokeApiKey, type ApiKeyMetadata, type ApiKeyMode } from './api-client';

/**
 * Extracted from `features/onboarding/ApiKeysContext.tsx` (TTP-243) so
 * TTP-244's dashboard session key (see `src/app/DashboardSessionKeyContext.tsx`)
 * can reuse the EXACT same mechanics instead of duplicating them — both
 * callers need "find-or-mint a single named key, replacing an orphan from a
 * previous page load", just for different `(mode, name)` pairs.
 *
 * The safety property this gives both callers for free: this function only
 * ever looks at — and only ever revokes — a key matching the exact
 * `(mode, name)` pair it was called with. `ApiKeysContext.tsx` calls it with
 * `'onboarding — sandbox'`/`'onboarding — produção'`; the dashboard session
 * key calls it with the reserved `__trivio_dashboard_session__` name. Those
 * names never collide, so the dashboard session logic can NEVER find, and
 * therefore can NEVER revoke, the two keys revealed once to the user at
 * onboarding's Tela 5 (`ApiKeyStep.tsx`) — see `ensure-api-key.test.ts` for
 * a test that pins exactly this.
 */

export interface HeldApiKey {
  id: string;
  key: string;
  prefix: string;
}

/**
 * Idempotently ensures exactly one active key named `name` with mode `mode`
 * exists, minting one if it's missing.
 *
 * - Already held in memory from earlier in this same session (`held.id`
 *   matches an existing, non-revoked key) → reused as-is, no network call
 *   beyond the caller-supplied `existingMetadata` lookup.
 * - Found in `existingMetadata` but NOT held in memory (a previous mint
 *   whose raw value this process no longer has — e.g. after a page reload)
 *   → that key is orphaned (its plaintext is gone forever; the backend
 *   returns `key` exactly once, `ApiKeysService#create`) and gets revoked,
 *   then replaced with a freshly minted one. This keeps the per-`(mode,
 *   name)` active-key count bounded at 1 instead of growing unboundedly on
 *   every reload.
 * - Not found at all → minted fresh.
 *
 * Never throws for a "just create it" path — only propagates whatever
 * `revokeApiKey`/`createApiKey` themselves reject with (network/API
 * errors), same as the callers already handled before this was extracted.
 */
export async function ensureOneApiKey(
  idToken: string,
  mode: ApiKeyMode,
  name: string,
  held: HeldApiKey | null,
  existingMetadata: ApiKeyMetadata[],
): Promise<HeldApiKey> {
  const existing = existingMetadata.find((k) => k.mode === mode && k.name === name && !k.revoked_at);

  if (existing && held && held.id === existing.id) {
    // Already minted earlier in this same in-memory session — no-op.
    return held;
  }

  if (existing) {
    // Orphaned from a previous mint whose raw value we no longer hold —
    // can never be recovered, so replace it instead of leaving it active
    // forever.
    await revokeApiKey(idToken, existing.id);
  }

  const created = await createApiKey(idToken, name, mode);
  return { id: created.id, key: created.key, prefix: created.prefix };
}
